import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, Package, Loader2, Save, X, CheckCircle, AlertCircle, FolderPlus, Bell, Star, BarChart3, ShoppingBag, Upload, Image, Megaphone, Download, Search, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import AnalyticsTab from "@/components/admin/AnalyticsTab";
import ReviewsTab from "@/components/admin/ReviewsTab";
import NotificationsTab from "@/components/admin/NotificationsTab";
import OrdersTab from "@/components/admin/OrdersTab";
import SiteSettingsTab from "@/components/admin/SiteSettingsTab";
import DiscoveryTab from "@/components/admin/DiscoveryTab";
import CouponsTab from "@/components/admin/CouponsTab";

var API = "/api";

function formatPrice(p) {
  return "$" + p.toFixed(2);
}

function ImageUploader({ images, onImagesChange }) {
  var fileInputRef = useRef(null);
  var [uploading, setUploading] = useState(false);
  var [urlInput, setUrlInput] = useState("");

  async function handleFileSelect(e) {
    var files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploading(true);
    var newUrls = [];
    
    for (var file of files) {
      try {
        var formData = new FormData();
        formData.append("file", file);
        var res = await axios.post(API + "/upload/image", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        newUrls.push(res.data.url);
      } catch (err) {
        toast.error("Failed to upload " + file.name);
      }
    }
    
    if (newUrls.length > 0) {
      onImagesChange([...images, ...newUrls]);
      toast.success(newUrls.length + " image(s) uploaded");
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function handleAddUrl() {
    if (urlInput && !images.includes(urlInput)) {
      onImagesChange([...images, urlInput]);
      setUrlInput("");
    }
  }

  function handleRemoveImage(url) {
    onImagesChange(images.filter(function(i) { return i !== url; }));
  }

  return (
    <div className="space-y-3">
      <Label className="text-[#A1A1AA] text-xs uppercase">Images</Label>
      
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="image-upload"
        />
        <Button
          type="button"
          onClick={function() { fileInputRef.current?.click(); }}
          disabled={uploading}
          className="btn-secondary flex-1"
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          {uploading ? "Uploading..." : "Upload Images"}
        </Button>
      </div>
      
      <div className="flex gap-2">
        <Input
          value={urlInput}
          onChange={function(e) { setUrlInput(e.target.value); }}
          placeholder="Or paste image URL"
          className="input-tactical"
        />
        <Button type="button" onClick={handleAddUrl} className="btn-secondary">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {images.map(function(url, idx) {
            return (
              <div key={url} className="relative group">
                <img src={url} alt="" className="w-20 h-20 object-cover rounded-sm border border-white/10" />
                <button
                  type="button"
                  onClick={function() { handleRemoveImage(url); }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
                {idx === 0 && <span className="absolute bottom-0 left-0 right-0 bg-[#C0A062] text-black text-[10px] text-center py-0.5">Primary</span>}
              </div>
            );
          })}
        </div>
      )}
      
      <p className="text-[10px] text-[#71717a]">First image will be the primary product image. Max 10MB per file.</p>
    </div>
  );
}

function ProductRow({ product, onEdit, onDelete }) {
  var imgUrl = product.images && product.images.length > 0 ? product.images[0] : null;
  var priceDisplay = product.sale_price || product.price;
  var qtyDisplay = product.quantity != null ? product.quantity : "∞";
  
  return (
    <tr className="border-b border-white/5 hover:bg-white/5">
      <td className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#1c1c1c] rounded-sm overflow-hidden">
            {imgUrl ? <img src={imgUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-[#3f3f46]" /></div>}
          </div>
          <div>
            <p className="font-heading font-bold text-white text-sm uppercase line-clamp-1">{product.name}</p>
            {product.featured && <span className="text-[10px] text-[#C0A062] font-mono uppercase">Featured</span>}
          </div>
        </div>
      </td>
      <td className="p-4"><span className="category-badge">{product.category}</span></td>
      <td className="p-4"><p className="text-white font-heading font-bold">{formatPrice(priceDisplay)}</p></td>
      <td className="p-4"><span className="text-[#A1A1AA] font-mono text-sm">{qtyDisplay}</span></td>
      <td className="p-4">
        {product.in_stock ? <span className="inline-flex items-center gap-1 text-[#8CB369] text-sm"><CheckCircle className="w-4 h-4" />In Stock</span> : <span className="inline-flex items-center gap-1 text-red-400 text-sm"><AlertCircle className="w-4 h-4" />Out</span>}
      </td>
      <td className="p-4">
        <div className="flex items-center justify-end gap-2">
          <button onClick={onEdit} className="p-2 text-[#A1A1AA] hover:text-[#C0A062]"><Pencil className="w-4 h-4" /></button>
          <button onClick={onDelete} className="p-2 text-[#A1A1AA] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
        </div>
      </td>
    </tr>
  );
}

function CategoryRow({ category, onEdit, onDelete, onAddSub, isSubcategory }) {
  return (
    <tr className={"border-b border-white/5 hover:bg-white/5" + (isSubcategory ? " bg-white/[0.02]" : "")}>
      <td className={"p-4 text-white font-heading font-bold" + (isSubcategory ? " pl-8" : "")}>{isSubcategory ? "↳ " : ""}{category.name}</td>
      <td className="p-4 text-[#A1A1AA] font-mono text-sm">{category.slug}</td>
      <td className="p-4"><span className={isSubcategory ? "text-[#71717a] text-xs font-mono uppercase" : "text-[#C0A062] text-xs font-mono uppercase"}>{isSubcategory ? "Sub" : "Parent"}</span></td>
      <td className="p-4 text-right">
        {!isSubcategory && (
          <button
            onClick={onAddSub}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 mr-2 border border-[#8CB369]/30 rounded-sm text-xs font-mono uppercase text-[#8CB369] hover:text-[#a3c97a] hover:border-[#8CB369]/60 hover:bg-[#8CB369]/5 transition-colors"
            data-testid={"add-sub-" + category.id}
            title={"Add a subcategory under " + category.name}
          >
            <Plus className="w-3 h-3" /> Sub
          </button>
        )}
        <button onClick={onEdit} className="p-2 text-[#A1A1AA] hover:text-[#C0A062]" data-testid={"edit-category-" + category.id}><Pencil className="w-4 h-4" /></button>
        <button onClick={onDelete} className="p-2 text-[#A1A1AA] hover:text-red-400" data-testid={"delete-category-" + category.id}><Trash2 className="w-4 h-4" /></button>
      </td>
    </tr>
  );
}

export default function AdminPage() {
  var [products, setProducts] = useState([]);
  var [categories, setCategories] = useState([]);
  var [notifications, setNotifications] = useState([]);
  var [reviews, setReviews] = useState([]);
  var [orders, setOrders] = useState([]);
  var [analytics, setAnalytics] = useState(null);
  var [analyticsError, setAnalyticsError] = useState(null);
  var [isLoading, setIsLoading] = useState(true);
  var [isModalOpen, setIsModalOpen] = useState(false);
  var [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  var [editingProduct, setEditingProduct] = useState(null);
  var [editingCategory, setEditingCategory] = useState(null);
  var [isSaving, setIsSaving] = useState(false);
  var [activeTab, setActiveTab] = useState("analytics");

  var [formName, setFormName] = useState("");
  var [formDesc, setFormDesc] = useState("");
  var [formPrice, setFormPrice] = useState("");
  var [formSalePrice, setFormSalePrice] = useState("");
  var [formCategory, setFormCategory] = useState("");
  var [formSubcategory, setFormSubcategory] = useState("");
  var [formImages, setFormImages] = useState([]);
  var [formInStock, setFormInStock] = useState(true);
  var [formQuantity, setFormQuantity] = useState("");
  var [formFeatured, setFormFeatured] = useState(false);
  var [formAddons, setFormAddons] = useState([]);
  var [formCustomShipping, setFormCustomShipping] = useState(false);
  var [formShippingCost, setFormShippingCost] = useState("");
  var [catName, setCatName] = useState("");
  var [catalogDownloading, setCatalogDownloading] = useState(false);

  function handleDownloadCatalog() {
    setCatalogDownloading(true);
    axios.get(API + "/products/catalog.pdf", { responseType: "blob", withCredentials: true })
      .then(function(res) {
        var url = window.URL.createObjectURL(res.data);
        var a = document.createElement("a");
        a.href = url;
        a.download = "RGP-Product-Catalog.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(function() { window.URL.revokeObjectURL(url); }, 100);
        toast.success("Catalog downloaded");
      })
      .catch(function() { toast.error("Failed to build catalog"); })
      .finally(function() { setCatalogDownloading(false); });
  }
  var [catSlug, setCatSlug] = useState("");
  var [catParent, setCatParent] = useState("");

  useEffect(function() { fetchAll(); }, []);

  function fetchAll() {
    axios.get(API + "/products").then(function(res) { setProducts(res.data); }).finally(function() { setIsLoading(false); });
    axios.get(API + "/categories").then(function(res) { setCategories(res.data.category_tree || []); });
    axios.get(API + "/notifications/subscribers", { withCredentials: true }).then(function(res) { setNotifications(res.data); }).catch(function() {});
    axios.get(API + "/reviews", { withCredentials: true }).then(function(res) { setReviews(res.data.reviews || res.data || []); }).catch(function() {});
    axios.get(API + "/orders", { withCredentials: true }).then(function(res) { setOrders(res.data); }).catch(function() {});
    axios.get(API + "/analytics", { withCredentials: true })
      .then(function(res) { setAnalytics(res.data); setAnalyticsError(null); })
      .catch(function(err) {
        var msg = (err && err.response && err.response.data && err.response.data.detail) || err.message || "Failed to load analytics";
        var status = err && err.response ? err.response.status : "no response";
        setAnalyticsError("HTTP " + status + " — " + msg);
        setAnalytics({ summary: { total_revenue: 0, today_revenue: 0, week_revenue: 0, month_revenue: 0, total_orders: 0, today_orders: 0, week_orders: 0, month_orders: 0, total_products: 0, total_customers: 0, pending_notifications: 0, total_reviews: 0 }, top_products: [], recent_orders: [] });
      });
  }

  function openModal(product, prefill) {
    if (product) {
      setEditingProduct(product);
      setFormName(product.name); setFormDesc(product.description); setFormPrice(product.price.toString());
      setFormSalePrice(product.sale_price ? product.sale_price.toString() : "");
      setFormCategory(product.category); setFormSubcategory(product.subcategory || "");
      setFormImages(product.images || []); setFormInStock(product.in_stock);
      setFormQuantity(product.quantity != null ? product.quantity.toString() : "");
      setFormFeatured(product.featured);
      setFormAddons(product.addons || []);
      setFormCustomShipping(product.shipping_cost != null);
      setFormShippingCost(product.shipping_cost != null ? product.shipping_cost.toString() : "");
    } else {
      setEditingProduct(null);
      setFormName(prefill?.name || "");
      setFormDesc(prefill?.description || "");
      setFormPrice(""); setFormSalePrice("");
      setFormCategory(""); setFormSubcategory(""); setFormImages([]); setFormInStock(true);
      setFormQuantity(""); setFormFeatured(false); setFormAddons([]);
      setFormCustomShipping(false); setFormShippingCost("");
    }
    setIsModalOpen(true);
  }

  function handleCreateListingFromDiscovery(result) {
    var sourceLabel = (result.source || "discovery").toUpperCase();
    var prefill = {
      name: result.keyword || result.title || "",
      description: "Reference (do not publish this raw): found via " + sourceLabel + " — " + (result.title || "") + "\n" + (result.url || "") + "\n\n" + (result.snippet || "")
    };
    setActiveTab("products");
    // Mark result as "have" so it drops out of the "new" queue
    if (result.id) {
      axios.patch(API + "/discovery/results/" + result.id, { status: "have" }, { withCredentials: true }).catch(function() {});
    }
    setTimeout(function() { openModal(null, prefill); toast.success("Product modal pre-filled from discovery result"); }, 150);
  }

  function closeModal() { setIsModalOpen(false); setEditingProduct(null); }

  function addAddon() {
    var newAddon = { id: "addon-" + Date.now(), name: "", price: 0, max_quantity: 10 };
    setFormAddons([...formAddons, newAddon]);
  }

  function updateAddon(index, field, value) {
    var updated = [...formAddons];
    updated[index] = { ...updated[index], [field]: field === "price" || field === "max_quantity" ? parseFloat(value) || 0 : value };
    setFormAddons(updated);
  }

  function removeAddon(index) {
    setFormAddons(formAddons.filter(function(_, i) { return i !== index; }));
  }

  function openCategoryModal(category, parentIdForNew) {
    if (category) {
      setEditingCategory(category);
      setCatName(category.name);
      setCatSlug(category.slug);
      setCatParent(category.parent_id || "__none__");
    } else {
      setEditingCategory(null);
      setCatName("");
      setCatSlug("");
      setCatParent(parentIdForNew || "__none__");
    }
    setIsCategoryModalOpen(true);
  }

  function closeCategoryModal() { setIsCategoryModalOpen(false); setEditingCategory(null); }

  function handleSubmit(e) {
    e.preventDefault(); setIsSaving(true);
    var validAddons = formAddons.filter(function(a) { return a.name && a.price > 0; });
    var customShip = formCustomShipping && formShippingCost ? parseFloat(formShippingCost) : null;
    var data = { name: formName, description: formDesc, price: parseFloat(formPrice),
      sale_price: formSalePrice ? parseFloat(formSalePrice) : null, category: formCategory,
      subcategory: formSubcategory || null, images: formImages, in_stock: formInStock,
      quantity: formQuantity ? parseInt(formQuantity) : null, featured: formFeatured,
      addons: validAddons.length > 0 ? validAddons : null,
      shipping_cost: customShip };
    var req = editingProduct ? axios.put(API + "/products/" + editingProduct.id, data, { withCredentials: true }) : axios.post(API + "/products", data, { withCredentials: true });
    req.then(function() { toast.success(editingProduct ? "Updated!" : "Created!"); fetchAll(); closeModal(); })
      .catch(function() { toast.error("Failed to save"); }).finally(function() { setIsSaving(false); });
  }

  function handleDelete(productId) {
    if (window.confirm("Delete this product?")) {
      axios.delete(API + "/products/" + productId, { withCredentials: true })
        .then(function() { toast.success("Deleted!"); fetchAll(); })
        .catch(function(err) { toast.error(err.response?.data?.detail || "Failed to delete"); });
    }
  }

  function handleCategorySubmit(e) {
    e.preventDefault(); setIsSaving(true);
    var params = new URLSearchParams({ name: catName, slug: catSlug });
    if (catParent && catParent !== "__none__") params.append("parent_id", catParent);
    var req = editingCategory
      ? axios.put(API + "/categories/" + editingCategory.id + "?" + params.toString(), null, { withCredentials: true })
      : axios.post(API + "/categories?" + params.toString(), null, { withCredentials: true });
    req.then(function() { toast.success(editingCategory ? "Updated!" : "Created!"); fetchAll(); closeCategoryModal(); })
      .catch(function(err) { toast.error(err.response?.data?.detail || "Failed"); }).finally(function() { setIsSaving(false); });
  }

  function handleDeleteCategory(categoryId) {
    if (window.confirm("Delete this category?")) {
      axios.delete(API + "/categories/" + categoryId, { withCredentials: true })
        .then(function() { toast.success("Deleted!"); fetchAll(); })
        .catch(function(err) { toast.error(err.response?.data?.detail || "Failed to delete"); });
    }
  }

  var flatCategories = [];
  categories.forEach(function(cat) {
    flatCategories.push({ ...cat, isSubcategory: false });
    if (cat.subcategories) cat.subcategories.forEach(function(sub) { flatCategories.push({ ...sub, isSubcategory: true }); });
  });

  var pendingNotifications = notifications.filter(function(n) { return !n.notified; });

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="admin-page">
      <div className="container-tactical">
        <div className="mb-8">
          <p className="font-mono text-[#C0A062] text-sm uppercase mb-2">Admin Panel</p>
          <h1 className="font-heading font-black text-4xl text-white uppercase">Dashboard</h1>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#121212] border border-white/10 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#C0A062] data-[state=active]:text-black"><BarChart3 className="w-4 h-4 mr-2" />Analytics</TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-[#C0A062] data-[state=active]:text-black"><Package className="w-4 h-4 mr-2" />Products</TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-[#C0A062] data-[state=active]:text-black"><FolderPlus className="w-4 h-4 mr-2" />Categories</TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#C0A062] data-[state=active]:text-black"><ShoppingBag className="w-4 h-4 mr-2" />Orders</TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-[#C0A062] data-[state=active]:text-black relative"><Bell className="w-4 h-4 mr-2" />Notifications{pendingNotifications.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">{pendingNotifications.length}</span>}</TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-[#C0A062] data-[state=active]:text-black"><Star className="w-4 h-4 mr-2" />Reviews</TabsTrigger>
            <TabsTrigger value="site" className="data-[state=active]:bg-[#C0A062] data-[state=active]:text-black"><Megaphone className="w-4 h-4 mr-2" />Site</TabsTrigger>
            <TabsTrigger value="discovery" className="data-[state=active]:bg-[#C0A062] data-[state=active]:text-black" data-testid="discovery-tab-trigger"><Search className="w-4 h-4 mr-2" />Discovery</TabsTrigger>
            <TabsTrigger value="coupons" className="data-[state=active]:bg-[#C0A062] data-[state=active]:text-black" data-testid="coupons-tab-trigger"><Tag className="w-4 h-4 mr-2" />Coupons</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics"><AnalyticsTab analytics={analytics} error={analyticsError} /></TabsContent>
          
          <TabsContent value="products">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <p className="text-[#A1A1AA] text-sm">{products.length} total products</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  className="border-white/10 text-[#A1A1AA] hover:text-[#C0A062] hover:border-[#C0A062]/40"
                  onClick={handleDownloadCatalog}
                  disabled={catalogDownloading || products.length === 0}
                  data-testid="download-product-catalog"
                  title="Download a printable PDF catalog of all products"
                >
                  {catalogDownloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  {catalogDownloading ? "Building Catalog..." : "Download Catalog (PDF)"}
                </Button>
                <Button className="btn-primary" onClick={function() { openModal(null); }}><Plus className="w-4 h-4 mr-2" />Add Product</Button>
              </div>
            </div>
            {isLoading ? <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 text-[#C0A062] animate-spin" /></div> : (
              <div className="bg-[#121212] border border-white/5 rounded-sm overflow-hidden overflow-x-auto">
                <table className="w-full"><thead><tr className="border-b border-white/10">
                  <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Product</th>
                  <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Category</th>
                  <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Price</th>
                  <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Qty</th>
                  <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Status</th>
                  <th className="text-right p-4 font-mono text-xs text-[#A1A1AA] uppercase">Actions</th>
                </tr></thead><tbody>
                  {products.map(function(product) { return <ProductRow key={product.id} product={product} onEdit={function() { openModal(product); }} onDelete={function() { handleDelete(product.id); }} />; })}
                </tbody></table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="categories">
            <div className="flex justify-end mb-4"><Button className="btn-primary" onClick={function() { openCategoryModal(null); }} data-testid="add-parent-category-btn"><FolderPlus className="w-4 h-4 mr-2" />Add Parent Category</Button></div>
            <div className="bg-[#121212] border border-white/5 rounded-sm overflow-hidden">
              <table className="w-full"><thead><tr className="border-b border-white/10">
                <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Name</th>
                <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Slug</th>
                <th className="text-left p-4 font-mono text-xs text-[#A1A1AA] uppercase">Type</th>
                <th className="text-right p-4 font-mono text-xs text-[#A1A1AA] uppercase">Actions</th>
              </tr></thead><tbody>
                {flatCategories.map(function(cat) { return <CategoryRow key={cat.id} category={cat} isSubcategory={cat.isSubcategory} onEdit={function() { openCategoryModal(cat); }} onDelete={function() { handleDeleteCategory(cat.id); }} onAddSub={function() { openCategoryModal(null, cat.id); }} />; })}
                {flatCategories.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-[#71717a]">No custom categories yet.</td></tr>}
              </tbody></table>
            </div>
          </TabsContent>

          <TabsContent value="orders"><OrdersTab orders={orders} onRefresh={function() { axios.get(API + "/orders", { withCredentials: true }).then(function(res) { setOrders(res.data); }).catch(function() {}); }} /></TabsContent>
          <TabsContent value="notifications"><NotificationsTab notifications={notifications} onRefresh={fetchAll} /></TabsContent>
          <TabsContent value="reviews"><ReviewsTab reviews={reviews} onRefresh={fetchAll} /></TabsContent>
          <TabsContent value="site"><SiteSettingsTab /></TabsContent>
          <TabsContent value="discovery"><DiscoveryTab onCreateListing={handleCreateListingFromDiscovery} /></TabsContent>
          <TabsContent value="coupons"><CouponsTab /></TabsContent>
        </Tabs>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading font-bold text-white text-xl uppercase">{editingProduct ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2"><Label className="text-[#A1A1AA] text-xs uppercase">Name *</Label><Input value={formName} onChange={function(e) { setFormName(e.target.value); }} required className="input-tactical" /></div>
              <div className="space-y-2"><Label className="text-[#A1A1AA] text-xs uppercase">Description *</Label><Textarea value={formDesc} onChange={function(e) { setFormDesc(e.target.value); }} required className="input-tactical min-h-[100px]" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2"><Label className="text-[#A1A1AA] text-xs uppercase">Price *</Label><Input type="number" step="0.01" value={formPrice} onChange={function(e) { setFormPrice(e.target.value); }} required className="input-tactical" /></div>
                <div className="space-y-2"><Label className="text-[#A1A1AA] text-xs uppercase">Sale Price</Label><Input type="number" step="0.01" value={formSalePrice} onChange={function(e) { setFormSalePrice(e.target.value); }} className="input-tactical" /></div>
                <div className="space-y-2"><Label className="text-[#A1A1AA] text-xs uppercase">Quantity</Label><Input type="number" min="0" value={formQuantity} onChange={function(e) { setFormQuantity(e.target.value); }} placeholder="∞" className="input-tactical" /><p className="text-[10px] text-[#71717a]">Leave empty for unlimited</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-[#A1A1AA] text-xs uppercase">Category *</Label>
                  <Select value={formCategory} onValueChange={function(v) { setFormCategory(v); setFormSubcategory(""); }}>
                    <SelectTrigger className="input-tactical" data-testid="product-category-select"><SelectValue placeholder={categories.length ? "Select" : "No categories — add some in the Categories tab"} /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10">
                      {categories.map(function(c) { return <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>; })}
                    </SelectContent>
                  </Select>
                  {formCategory && !categories.find(function(c) { return c.slug === formCategory; }) && (
                    <p className="text-[10px] text-amber-400">Saved category &quot;{formCategory}&quot; is no longer in your list. Pick a new one or re-create it in the Categories tab.</p>
                  )}
                </div>
                <div className="space-y-2"><Label className="text-[#A1A1AA] text-xs uppercase">Subcategory</Label>
                  {(function() {
                    var parent = categories.find(function(c) { return c.slug === formCategory; });
                    var subs = (parent && parent.subcategories) || [];
                    if (subs.length === 0) {
                      return <Input value={formSubcategory} onChange={function(e) { setFormSubcategory(e.target.value); }} placeholder="Optional" className="input-tactical" data-testid="product-subcategory-input" />;
                    }
                    return (
                      <Select value={formSubcategory || "__none__"} onValueChange={function(v) { setFormSubcategory(v === "__none__" ? "" : v); }}>
                        <SelectTrigger className="input-tactical" data-testid="product-subcategory-select"><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10">
                          <SelectItem value="__none__">None</SelectItem>
                          {subs.map(function(s) { return <SelectItem key={s.id} value={s.slug}>{s.name}</SelectItem>; })}
                        </SelectContent>
                      </Select>
                    );
                  })()}
                </div>
              </div>
              <ImageUploader images={formImages} onImagesChange={setFormImages} />
              <div className="flex gap-8">
                <div className="flex items-center justify-between flex-1"><Label className="text-[#A1A1AA] text-sm">In Stock</Label><Switch checked={formInStock} onCheckedChange={setFormInStock} /></div>
                <div className="flex items-center justify-between flex-1"><Label className="text-[#A1A1AA] text-sm">Featured</Label><Switch checked={formFeatured} onCheckedChange={setFormFeatured} /></div>
              </div>

              <div className="space-y-3 p-4 bg-[#1a1a1a] border border-white/10 rounded-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-[#A1A1AA] text-sm">Custom Shipping Cost</Label>
                    <p className="text-[10px] text-[#71717a] mt-1">Override the standard $12.50 flat-rate for large or heavy items.</p>
                  </div>
                  <Switch
                    checked={formCustomShipping}
                    onCheckedChange={setFormCustomShipping}
                    data-testid="custom-shipping-toggle"
                  />
                </div>
                {formCustomShipping && (
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] text-xs uppercase">Shipping Cost ($) — for the first unit</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formShippingCost}
                      onChange={function(e) { setFormShippingCost(e.target.value); }}
                      placeholder="e.g. 25.00"
                      className="input-tactical"
                      required={formCustomShipping}
                      data-testid="custom-shipping-input"
                    />
                    <p className="text-[10px] text-[#71717a]">
                      Each additional unit of this item adds 50% of this rate. e.g. ${formShippingCost || "25"} → 1 unit ${parseFloat(formShippingCost || 0).toFixed(2)}, 2 units ${(parseFloat(formShippingCost || 0) * 1.5).toFixed(2)}, 3 units ${(parseFloat(formShippingCost || 0) * 2).toFixed(2)}.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="space-y-3 p-4 bg-[#1a1a1a] border border-white/10 rounded-sm">
                <div className="flex items-center justify-between">
                  <Label className="text-[#C0A062] text-xs uppercase font-mono">Product Add-ons (Optional)</Label>
                  <Button type="button" onClick={addAddon} className="btn-secondary text-xs py-1 px-2"><Plus className="w-3 h-3 mr-1" />Add Option</Button>
                </div>
                {formAddons.length === 0 && <p className="text-[#71717a] text-xs">No add-ons. Use this for optional extras like replacement springs, accessories, etc.</p>}
                {formAddons.map(function(addon, idx) {
                  return (
                    <div key={addon.id} className="flex gap-2 items-end">
                      <div className="flex-1 space-y-1">
                        <Label className="text-[#A1A1AA] text-[10px] uppercase">Name</Label>
                        <Input value={addon.name} onChange={function(e) { updateAddon(idx, "name", e.target.value); }} placeholder="e.g. Replacement Spring" className="input-tactical text-sm" />
                      </div>
                      <div className="w-24 space-y-1">
                        <Label className="text-[#A1A1AA] text-[10px] uppercase">Price ($)</Label>
                        <Input type="number" step="0.01" value={addon.price} onChange={function(e) { updateAddon(idx, "price", e.target.value); }} className="input-tactical text-sm" />
                      </div>
                      <div className="w-20 space-y-1">
                        <Label className="text-[#A1A1AA] text-[10px] uppercase">Max Qty</Label>
                        <Input type="number" min="1" value={addon.max_quantity} onChange={function(e) { updateAddon(idx, "max_quantity", e.target.value); }} className="input-tactical text-sm" />
                      </div>
                      <Button type="button" onClick={function() { removeAddon(idx); }} className="text-red-400 hover:text-red-300 p-2"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  );
                })}
              </div>
              
              <p className="text-xs text-[#71717a]">Note: If quantity reaches 0, product auto-marks out of stock & notifies subscribers.</p>
              <div className="flex gap-4 pt-4"><Button type="button" onClick={closeModal} className="btn-secondary flex-1">Cancel</Button><Button type="submit" className="btn-primary flex-1" disabled={isSaving}>{isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{editingProduct ? "Update" : "Create"}</Button></div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
          <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-md">
            <DialogHeader><DialogTitle className="font-heading font-bold text-white text-xl uppercase">{editingCategory ? "Edit Category" : (catParent && catParent !== "__none__" ? "Add Subcategory" : "Add Parent Category")}</DialogTitle></DialogHeader>
            <form onSubmit={handleCategorySubmit} className="space-y-4">
              <div className="space-y-2"><Label className="text-[#A1A1AA] text-xs uppercase">Name *</Label><Input value={catName} onChange={function(e) { setCatName(e.target.value); setCatSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')); }} required className="input-tactical" placeholder="e.g. Machine Guns" /></div>
              <div className="space-y-2"><Label className="text-[#A1A1AA] text-xs uppercase">Slug *</Label><Input value={catSlug} onChange={function(e) { setCatSlug(e.target.value); }} required className="input-tactical" placeholder="e.g. machine-guns" /><p className="text-[10px] text-[#71717a]">URL-friendly identifier</p></div>
              <div className="space-y-2"><Label className="text-[#A1A1AA] text-xs uppercase">Parent Category</Label>
                <Select value={catParent} onValueChange={setCatParent}><SelectTrigger className="input-tactical"><SelectValue placeholder="None (Top Level)" /></SelectTrigger><SelectContent className="bg-[#121212] border-white/10"><SelectItem value="__none__">None (Top Level)</SelectItem>{categories.filter(function(c) { return !c.parent_id; }).map(function(c) { return <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>; })}</SelectContent></Select>
              </div>
              <div className="flex gap-4 pt-4"><Button type="button" onClick={closeCategoryModal} className="btn-secondary flex-1">Cancel</Button><Button type="submit" className="btn-primary flex-1" disabled={isSaving}>{isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{editingCategory ? "Update" : "Create"}</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
